import { cn } from "@-/com.npmjs.cn";
import { cva, type VariantProps } from "@-/com.npmjs.cva";
import type { ComponentProps } from "react";

// Muted tones mix the surrounding text color with transparency (`text-current/…`) instead of
// naming black or white, so one component reads correctly on paper, canvas and ink sections.

const websiteEyebrowVariants = cva({
  base: "m-0 text-[0.7rem] font-bold tracking-[0.2em] uppercase sm:text-xs",
  defaultVariants: {
    tone: "accent",
  },
  variants: {
    tone: {
      accent: "text-website-accent",
      muted: "text-current/40",
    },
  },
});

/** The small uppercase label above a heading. */
export function WebsiteEyebrow({
  className,
  tone,
  ...props
}: ComponentProps<"p"> & VariantProps<typeof websiteEyebrowVariants>) {
  return <p className={cn(websiteEyebrowVariants({ tone }), className)} {...props} />;
}

const websiteHeadingVariants = cva({
  base: "m-0 font-semibold text-balance",
  defaultVariants: {
    size: "section",
  },
  variants: {
    size: {
      /** The landing hero's headline. */
      display:
        "text-[clamp(2.45rem,9vw,4.25rem)] leading-[0.98] tracking-[-0.05em] lg:text-[clamp(3.25rem,4.8vw,4.75rem)]",
      /** Feature headlines beside an illustration. */
      feature: "text-[clamp(2rem,3.6vw,3rem)] leading-[1.02] tracking-[-0.045em]",
      /** Small titles: tour stops, list items. */
      label: "text-lg leading-snug tracking-[-0.02em]",
      /** Section headlines and the headline of every page other than the landing. */
      section: "text-[clamp(2.4rem,5vw,4rem)] leading-[1.02] tracking-[-0.045em]",
      /** Card and document-section titles. */
      title: "text-xl leading-tight tracking-[-0.03em] sm:text-2xl",
    },
  },
});

export type WebsiteHeadingProps = ComponentProps<"h2"> &
  VariantProps<typeof websiteHeadingVariants> & {
    /** The document outline level; the visual size is chosen independently with `size`. */
    level: 1 | 2 | 3;
  };

export function WebsiteHeading({ className, level, size, ...props }: WebsiteHeadingProps) {
  const HeadingElement = `h${level}` as const;
  return <HeadingElement className={cn(websiteHeadingVariants({ size }), className)} {...props} />;
}

const websiteTextVariants = cva({
  base: "m-0",
  defaultVariants: {
    size: "body",
    tone: "muted",
  },
  variants: {
    size: {
      body: "text-[0.95rem] leading-7 sm:text-base",
      caption: "text-sm leading-6",
      fine: "text-[0.8rem] font-medium",
      lead: "text-base leading-7 sm:text-lg sm:leading-8",
    },
    tone: {
      default: "",
      muted: "text-current/60",
      subtle: "text-current/45",
    },
  },
});

/** A paragraph of website copy. */
export function WebsiteText({
  className,
  size,
  tone,
  ...props
}: ComponentProps<"p"> & VariantProps<typeof websiteTextVariants>) {
  return <p className={cn(websiteTextVariants({ size, tone }), className)} {...props} />;
}

/** An inline link inside website copy. */
export function WebsiteTextLink({ className, ...props }: ComponentProps<"a">) {
  return (
    <a
      className={cn(
        "font-medium text-website-accent underline decoration-website-accent/35 underline-offset-[0.2em] transition-colors hover:decoration-website-accent",
        className,
      )}
      {...props}
    />
  );
}
